<template>
  <div class="instructor-admin-page">
    <div class="d-flex">
      <nav id="breadcrumbs" class="mb-3">
        <ul>
          <li>
            <router-link :to="{ name: 'company.dashboard' }">
              <i class="uil-home-alt"></i>
            </router-link>
          </li>
          <li>{{ $t("general.instructors") }}</li>
          <li>{{ $t("general.instructor_list") }}</li>
        </ul>
      </nav>
    </div>

    <div class="section-header mb-lg-2 border-0 uk-flex-middle">
      <div class="section-header-left">
        <h2 class="uk-heading-line text-left">
          <span>{{ $t("general.instructor_list") }}</span>
        </h2>
      </div>
      <div class="section-header-right">
        <button
          class="btn btn-default"
          type="button"
          :disabled="!$hasPermission('create_instructor')"
          @click="goToCreatePage"
        >
          <i class="uil-plus"></i> {{ $t("general.add_instructor") }}
        </button>
      </div>
    </div>

    <div
      v-if="notificationMessage"
      :class="
        notificationType === 'error' ? 'uk-alert-danger' : 'uk-alert-success'
      "
      uk-alert
    >
      <a class="uk-alert-close" uk-close></a>
      <p
        class="mb-0"
        :class="notificationType === 'error' ? 'text-danger' : 'text-success'"
      >
        <i
          :class="
            notificationType === 'error'
              ? 'uil-exclamation-triangle'
              : 'uil-check-circle'
          "
        ></i>
        {{ notificationMessage }}
      </p>
    </div>

    <div class="card">
      <div class="card-header actions-toolbar border-0 instructor-toolbar-card">
        <div class="d-flex justify-content-between instructor-toolbar">
          <div class="uk-search uk-search-navbar instructor-search">
            <i class="uil-search position-absolute"></i>
            <input
              v-model.trim="filters.search"
              class="uk-search-input shadow-0 uk-form-small"
              type="search"
              :placeholder="$t('general.search')"
            />
          </div>

          <div class="d-flex instructor-toolbar-actions">
            <div class="btn-group mr-3">
              <select v-model.number="perPage" class="ml-1" @change="applyFilters">
                <option :value="10">10</option>
                <option :value="50">50</option>
                <option :value="100">100</option>
                <option :value="500">500</option>
                <option :value="1000">1000</option>
                <option :value="2500">2500</option>
              </select>
            </div>

            <a
              :uk-tooltip="$t('filters.order.sort')"
              class="btn btn-icon btn-hover btn-circle"
              href="javascript:void(0)"
            >
              <i class="uil-list-ui-alt"></i>
            </a>
            <div
              class="uk-dropdown"
              uk-dropdown="pos: bottom-right; mode: click; animation: uk-animation-scale-up"
            >
              <ul class="uk-nav uk-dropdown-nav">
                <li>
                  <a href="javascript:void(0)" @click="sortInstructors('asc', 'name')">
                    {{ $t("sorting.a_z") }}
                  </a>
                </li>
                <li>
                  <a href="javascript:void(0)" @click="sortInstructors('desc', 'name')">
                    {{ $t("sorting.z_a") }}
                  </a>
                </li>
                <li>
                  <a href="javascript:void(0)" @click="sortInstructors('asc', 'id')">
                    {{ $t("sorting.created_at_old") }}
                  </a>
                </li>
                <li>
                  <a href="javascript:void(0)" @click="sortInstructors('desc', 'id')">
                    {{ $t("sorting.created_at_new") }}
                  </a>
                </li>
              </ul>
            </div>

            <a
              :class="{ 'instructor-filter-button--active': hasActiveListFilters }"
              :uk-tooltip="$t('general.filter_list')"
              class="btn btn-icon btn-hover btn-circle"
              href="javascript:void(0)"
            >
              <i class="uil-filter"></i>
            </a>
            <div
              ref="instructorFilterDropdown"
              class="uk-dropdown large instructor-filter-dropdown"
              uk-dropdown="pos: bottom-right; mode: click; animation: uk-animation-scale-up"
            >
              <h4 class="mb-3 instructor-filter-title">
                <i class="uil-filter"></i> {{ $t("general.filter_list") }}
              </h4>

              <div class="instructor-filter-container">
                <div class="uk-child-width-1-2@s uk-grid-small" uk-grid>
                  <div>
                    <h5 class="uk-text-bold mb-2">
                      {{ $t("general.instructor_type") }}
                    </h5>
                    <select
                      v-model="filterDraft.type"
                      class="form-control instructor-filter-select"
                    >
                      <option value="">
                        {{ $t("general.all_instructor_types") }}
                      </option>
                      <option :value="instructorTypes.INTERNAL">
                        {{ $t("general.internal_instructor") }}
                      </option>
                      <option :value="instructorTypes.EXTERNAL">
                        {{ $t("general.external_instructor") }}
                      </option>
                    </select>
                  </div>

                  <div>
                    <h5 class="uk-text-bold mb-2">
                      {{ $t("users.title") }}
                    </h5>
                    <multiselect
                      v-model="filterDraft.titles"
                      :allow-empty="true"
                      :clear-on-select="false"
                      :close-on-select="false"
                      :deselect-label="$t('general.deselect')"
                      :loading="companyTagOptionsLoading"
                      :multiple="true"
                      :options="titleOptions"
                      :placeholder="$t('general.select')"
                      :preserve-search="true"
                      :searchable="true"
                      :select-label="$t('general.select')"
                      :selected-label="$t('general.selected')"
                      label="name"
                      track-by="id"
                    />
                  </div>

                  <div
                    v-for="field in companyTagFilterFields"
                    :key="field.key"
                  >
                    <h5 class="uk-text-bold mb-2">{{ $t(field.label) }}</h5>
                    <multiselect
                      v-model="filterDraft[field.key]"
                      :allow-empty="true"
                      :clear-on-select="false"
                      :close-on-select="false"
                      :deselect-label="$t('general.deselect')"
                      :loading="companyTagOptionsLoading"
                      :multiple="true"
                      :options="field.options"
                      :placeholder="$t('general.select')"
                      :preserve-search="true"
                      :searchable="true"
                      :select-label="$t('general.select')"
                      :selected-label="$t('general.selected')"
                      label="name"
                      track-by="id"
                    />
                  </div>

                  <div>
                    <h5 class="uk-text-bold mb-2">
                      {{ $t("general.corporate") }}
                    </h5>
                    <corporate-select
                      v-model="filterDraft.corporates"
                      :company-id="currentCompanyId"
                      :prefill-from-self="true"
                    />
                  </div>

                  <div class="uk-width-1-1">
                    <h5 class="uk-text-bold mb-2">
                      {{ $t("general.instructor_status") }}
                    </h5>
                    <div class="instructor-status-options">
                      <label>
                        <input
                          v-model="filterDraft.status"
                          class="uk-radio"
                          type="radio"
                          value=""
                        />
                        {{ $t("general.all") }}
                      </label>
                      <label>
                        <input
                          v-model="filterDraft.status"
                          :value="instructorStatuses.ACTIVE"
                          class="uk-radio"
                          type="radio"
                        />
                        {{ $t("general.active") }}
                      </label>
                      <label>
                        <input
                          v-model="filterDraft.status"
                          :value="instructorStatuses.PASSIVE"
                          class="uk-radio"
                          type="radio"
                        />
                        {{ $t("general.passive") }}
                      </label>
                    </div>
                  </div>
                </div>

                <div class="uk-flex uk-flex-right mt-3 instructor-filter-actions">
                  <button class="btn btn-light mr-2" type="button" @click="clearListFilters">
                    {{ $t("general.clear_all") }}
                  </button>
                  <button class="btn btn-primary grey" type="button" @click="applyFilters">
                    <i class="uil-filter"></i> {{ $t("general.filter") }}
                  </button>
                </div>
              </div>
            </div>

            <excel-download-button
              :loading="isReportDownloading"
              :tooltipText="$t('filters.export_excell')"
              @download="exportInstructors"
            />
          </div>
        </div>
      </div>

      <default-loading v-if="loading"></default-loading>

      <div v-else-if="loadError" class="instructor-list-state">
        <i class="uil-exclamation-circle text-danger"></i>
        <span>{{ loadError }}</span>
        <button
          class="btn btn-light btn-sm"
          type="button"
          @click="loadInstructors"
        >
          {{ $t("general.try_again") }}
        </button>
      </div>

      <template v-else>
        <div class="d-flex justify-content-between mr-3 ml-3 instructor-bulk-summary">
          <div>
            <span class="mr-2">
              {{ $t("general.active_instructors") }}: {{ counts.active }}
            </span>
            <span class="mr-2">
              {{ $t("general.passive_instructors") }}: {{ counts.passive }}
            </span>
            <span class="mr-2">
              {{ $t("general.total_instructors") }}: {{ counts.total }}
            </span>
          </div>
          <span>
            {{ $t("general.selected_instructors") }}:
            <i class="uil-graduation-hat"></i>
            {{ selectedInstructorIds.length }}
          </span>
        </div>

        <div class="p-3 d-flex disabled_hover users-bulk-actions instructor-bulk-actions">
          <button
            class="btn btn-sm btn-secondary mr-2 mr-auto p-2"
            type="button"
            :disabled="!instructors.length || bulkProcessing"
            @click="toggleAllVisibleInstructors"
          >
            <i class="uil-check-square"></i>
            {{
              $t(
                allVisibleSelected
                  ? "general.deselect_all"
                  : "general.select_all"
              )
            }}
          </button>
          <button
            class="btn btn-sm btn-success"
            type="button"
            :disabled="
              !hasSelection ||
              bulkProcessing ||
              !$hasPermission('update_instructor')
            "
            @click="bulkUpdateInstructorStatus(instructorStatuses.ACTIVE)"
          >
            <i class="uil-check"></i>
            {{ $t("general.activate_selected_instructors") }}
          </button>
          <button
            class="btn btn-sm btn-danger ml-2"
            type="button"
            :disabled="
              !hasSelection ||
              bulkProcessing ||
              !$hasPermission('update_instructor')
            "
            @click="bulkUpdateInstructorStatus(instructorStatuses.PASSIVE)"
          >
            <i class="uil-minus-circle"></i>
            {{ $t("general.deactivate_selected_instructors") }}
          </button>
          <button
            class="btn btn-sm btn-secondary ml-2"
            type="button"
            :disabled="
              !hasSelection ||
              bulkProcessing ||
              !$hasPermission('delete_instructor')
            "
            @click="bulkDeleteInstructors"
          >
            <i class="uil-trash"></i>
            {{ $t("general.delete_selected_instructors") }}
          </button>
        </div>

        <div class="table-responsive">
          <table class="table align-items-center instructor-table">
            <thead>
              <tr>
                <th scope="col"></th>
                <th scope="col">{{ $t("general.instructor") }}</th>
              <th scope="col">{{ $t("general.job_title") }}</th>
              <th scope="col">{{ $t("general.expertise_areas") }}</th>
              <th scope="col">{{ $t("general.instructor_type") }}</th>
              <th scope="col">{{ $t("general.status") }}</th>
              <th scope="col">{{ $t("general.created_at") }}</th>
              <th scope="col">
                <span class="sr-only">{{ $t("general.actions") }}</span>
              </th>
              </tr>
            </thead>
            <tbody class="list">
              <tr v-for="instructor in instructors" :key="instructor.id">
                <th>
                  <input
                    :checked="selectedInstructorIds.includes(instructor.id)"
                    :id="`instructor-checkbox-${instructor.id}`"
                    :value="instructor.id"
                    class="instructor-checkbox"
                    type="checkbox"
                    @change="
                      toggleInstructorSelection(
                        instructor.id,
                        $event.target.checked
                      )
                    "
                  />
                </th>
                <th scope="row">
                <div class="media align-items-center instructor-identity">
                  <img
                    :src="instructor.photo"
                    :alt="instructor.name"
                    class="instructor-avatar"
                    @error="useFallbackPhoto"
                  />
                  <div class="media-body ml-3">
                    <button
                      class="instructor-name-button"
                      type="button"
                      :disabled="!$hasPermission('show_instructor')"
                      @click="openProfileModal(instructor)"
                    >
                      {{ instructor.name }}
                    </button>
                    <small class="d-block text-muted">
                      {{
                        instructor.institutionName ||
                        instructor.department ||
                        "—"
                      }}
                    </small>
                  </div>
                </div>
              </th>
              <td>
                <span class="instructor-title">
                  {{ instructor.title || "—" }}
                </span>
              </td>
              <td>
                <div
                  v-if="
                    instructor.expertiseAreas &&
                    instructor.expertiseAreas.length
                  "
                  class="instructor-tags"
                >
                  <span
                    v-for="expertise in instructor.expertiseAreas.slice(0, 2)"
                    :key="expertise"
                    class="instructor-tag"
                  >
                    {{ expertise }}
                  </span>
                  <span
                    v-if="instructor.expertiseAreas.length > 2"
                    class="instructor-tag instructor-tag--more"
                    :uk-tooltip="getExpertiseTooltip(instructor.expertiseAreas)"
                  >
                    +{{ instructor.expertiseAreas.length - 2 }}
                  </span>
                </div>
                <span v-else class="text-muted">—</span>
              </td>
              <td>
                <span
                  class="instructor-type"
                  :class="`instructor-type--${instructor.type}`"
                >
                  {{ getTypeLabel(instructor.type) }}
                </span>
              </td>
              <td>
                <span
                  class="instructor-status"
                  :class="`instructor-status--${getStatusClass(
                    instructor.statusId
                  )}`"
                >
                  <span class="instructor-status__dot"></span>
                  {{ getStatusLabel(instructor.statusId) }}
                </span>
              </td>
              <td>
                <span class="instructor-date">
                  {{ formatDate(instructor.createdAt) }}
                </span>
              </td>
              <td class="text-right">
                <div class="actions instructor-actions">
                  <button
                    v-if="$hasPermission('show_instructor')"
                    class="btn btn-icon btn-hover btn-sm btn-circle"
                    type="button"
                    :uk-tooltip="$t('general.view_detail')"
                    @click="openProfileModal(instructor)"
                  >
                    <i class="uil-eye"></i>
                  </button>
                  <button
                    v-if="$hasPermission('update_instructor')"
                    class="btn btn-icon btn-hover btn-sm btn-circle"
                    type="button"
                    :uk-tooltip="$t('general.edit')"
                    @click="goToEditPage(instructor)"
                  >
                    <i class="uil-edit"></i>
                  </button>
                  <button
                    v-if="$hasPermission('update_instructor')"
                    class="btn btn-icon btn-hover btn-sm btn-circle"
                    type="button"
                    :uk-tooltip="
                      instructor.statusId === instructorStatuses.ACTIVE
                        ? $t('general.do_passive')
                        : $t('general.do_active')
                    "
                    @click="toggleInstructorStatus(instructor)"
                  >
                    <i
                      :class="
                        instructor.statusId === instructorStatuses.ACTIVE
                          ? 'uil-pause-circle text-warning'
                          : 'uil-play-circle text-success'
                      "
                    ></i>
                  </button>
                  <button
                    v-if="$hasPermission('delete_instructor')"
                    class="btn btn-icon btn-hover btn-sm btn-circle"
                    type="button"
                    :uk-tooltip="$t('general.delete')"
                    @click="deleteInstructor(instructor)"
                  >
                    <i class="uil-trash-alt text-danger"></i>
                  </button>
                </div>
              </td>
              </tr>
            </tbody>
          </table>
        </div>
        <pagination
          v-if="pagination"
          :pagination="pagination"
          class="mt-2 mb-3"
          @changePage="setPage($event)"
        ></pagination>
      </template>
    </div>

    <instructor-profile
      v-if="profileTarget"
      ref="instructorProfile"
      :key="`instructor-profile-${profileTarget.id}`"
      :instructor="profileTarget"
      :loading="profileLoading"
      @edit="editProfileTarget"
      @modalClosed="closeProfileModal"
    />

  </div>
</template>

<script>
import instructorService, {
  INSTRUCTOR_STATUSES,
  INSTRUCTOR_TYPES,
} from "@/core/services/instructor.service";
import Pagination from "@/assets/components/Pagination.vue";
import DefaultLoading from "@/view/components/loading/DefaultLoading.vue";
import ExcelDownloadButton from "@/view/components/buttons/ExcelDownloadButton.vue";
import { confirmDialogDelete } from "@/utils/confirmDialog";
import Multiselect from "vue-multiselect";
import InstructorProfile from "@/view/company/Instructors/components/InstructorProfile.vue";
import CorporateSelect from "@/view/components/select/CorporateSelect.vue";
import store from "@/core/services";
import positionModule, {
  BASE_URL as POSITION_BASE_URL,
  GET_ITEMS as GET_POSITIONS,
  ITEMS as POSITION_ITEMS,
  MODULE_NAME as POSITION_MODULE,
} from "@/core/services/store/position.module";
import titleModule, {
  BASE_URL as TITLE_BASE_URL,
  GET_ITEMS as GET_TITLES,
  ITEMS as TITLE_ITEMS,
  MODULE_NAME as TITLE_MODULE,
} from "@/core/services/store/title.module";
import regionModule, {
  BASE_URL as REGION_BASE_URL,
  GET_ITEMS as GET_REGIONS,
  ITEMS as REGION_ITEMS,
  MODULE_NAME as REGION_MODULE,
} from "@/core/services/store/region.module";
import departmentModule, {
  BASE_URL as DEPARTMENT_BASE_URL,
  GET_ITEMS as GET_DEPARTMENTS,
  ITEMS as DEPARTMENT_ITEMS,
  MODULE_NAME as DEPARTMENT_MODULE,
} from "@/core/services/store/department.module";
import branchModule, {
  BASE_URL as BRANCH_BASE_URL,
  GET_ITEMS as GET_BRANCHES,
  ITEMS as BRANCH_ITEMS,
  MODULE_NAME as BRANCH_MODULE,
} from "@/core/services/store/branch.module";
const FALLBACK_PHOTO = "https://cdn.anzera.com.tr/softinya/not-found.png";
const normalizeCompanyTagOptions = (items) => {
  const source = Array.isArray(items)
    ? items
    : Array.isArray(items?.data)
      ? items.data
      : [];

  return source
    .map((item) => ({ id: Number(item.id), name: item.name || "" }))
    .filter((item) => Number.isFinite(item.id) && item.name);
};

const createFilterSelection = () => ({
  type: "",
  status: "",
  titles: [],
  positions: [],
  regions: [],
  departments: [],
  branches: [],
  corporates: [],
});

export default {
  name: "InstructorList",
  components: {
    CorporateSelect,
    DefaultLoading,
    ExcelDownloadButton,
    InstructorProfile,
    Multiselect,
    Pagination,
  },
  beforeCreate() {
    const registerStoreModule = (moduleName, module) => {
      if (!store.state[moduleName]) store.registerModule(moduleName, module);
    };

    registerStoreModule(POSITION_MODULE, positionModule);
    registerStoreModule(TITLE_MODULE, titleModule);
    registerStoreModule(REGION_MODULE, regionModule);
    registerStoreModule(DEPARTMENT_MODULE, departmentModule);
    registerStoreModule(BRANCH_MODULE, branchModule);
  },
  data() {
    return {
      instructors: [],
      selectedInstructorIds: [],
      pagination: null,
      page: 1,
      perPage: 10,
      sortField: "id",
      sortDirection: "desc",
      loading: false,
      bulkProcessing: false,
      isReportDownloading: false,
      companyTagOptionsLoading: false,
      loadError: "",
      counts: {
        active: 0,
        passive: 0,
        total: 0,
      },
      filters: { search: "", ...createFilterSelection() },
      filterDraft: createFilterSelection(),
      profileTarget: null,
      profileLoading: false,
      profileRequestId: 0,
      notificationMessage: "",
      notificationType: "success",
      notificationTimer: null,
      filterTimer: null,
      instructorTypes: INSTRUCTOR_TYPES,
      instructorStatuses: INSTRUCTOR_STATUSES,
    };
  },
  computed: {
    titleOptions() {
      return normalizeCompanyTagOptions(
        store.getters[`${TITLE_MODULE}/${TITLE_ITEMS}`]
      );
    },
    positionOptions() {
      return normalizeCompanyTagOptions(
        store.getters[`${POSITION_MODULE}/${POSITION_ITEMS}`]
      );
    },
    regionOptions() {
      return normalizeCompanyTagOptions(
        store.getters[`${REGION_MODULE}/${REGION_ITEMS}`]
      );
    },
    departmentOptions() {
      return normalizeCompanyTagOptions(
        store.getters[`${DEPARTMENT_MODULE}/${DEPARTMENT_ITEMS}`]
      );
    },
    branchOptions() {
      return normalizeCompanyTagOptions(
        store.getters[`${BRANCH_MODULE}/${BRANCH_ITEMS}`]
      );
    },
    companyTagFilterFields() {
      return [
        {
          key: "positions",
          label: "users.mission",
          options: this.positionOptions,
        },
        { key: "regions", label: "users.area", options: this.regionOptions },
        {
          key: "departments",
          label: "users.department",
          options: this.departmentOptions,
        },
        { key: "branches", label: "users.branch", options: this.branchOptions },
      ];
    },
    currentCompanyId() {
      try {
        const storedUser = this.secureLocalStorage("user");
        const user = typeof storedUser === "string"
          ? JSON.parse(storedUser) || {}
          : storedUser || {};
        return user.company_id || null;
      } catch (error) {
        return null;
      }
    },
    hasActiveListFilters() {
      return Boolean(
        this.filters.type ||
        this.filters.status !== "" ||
        this.filters.titles.length ||
        this.filters.positions.length ||
        this.filters.regions.length ||
        this.filters.departments.length ||
        this.filters.branches.length ||
        this.filters.corporates.length
      );
    },
    hasSelection() {
      return this.selectedInstructorIds.length > 0;
    },
    allVisibleSelected() {
      return (
        this.instructors.length > 0 &&
        this.instructors.every((instructor) =>
          this.selectedInstructorIds.includes(instructor.id)
        )
      );
    },
  },
  watch: {
    "filters.search"() {
      this.page = 1;
      window.clearTimeout(this.filterTimer);
      this.filterTimer = window.setTimeout(() => {
        this.loadInstructors();
      }, 500);
    },
    "$route.query.profile"(newProfileId, oldProfileId) {
      const instructorId = Number(newProfileId);
      if (
        newProfileId
        && newProfileId !== oldProfileId
        && Number.isFinite(instructorId)
        && instructorId > 0
      ) {
        this.openProfileModalFromUrl(instructorId);
        return;
      }

      if (!newProfileId && oldProfileId && this.profileTarget) {
        this.$nextTick(() => {
          if (this.$refs.instructorProfile) {
            this.$refs.instructorProfile.closeModal();
          } else {
            this.closeProfileModal();
          }
        });
      }
    },
  },
  methods: {
    toggleInstructorSelection(id, checked) {
      if (checked) {
        if (!this.selectedInstructorIds.includes(id)) {
          this.selectedInstructorIds.push(id);
        }
        return;
      }

      this.selectedInstructorIds = this.selectedInstructorIds.filter(
        (instructorId) => instructorId !== id
      );
    },
    toggleAllVisibleInstructors() {
      const visibleIds = this.instructors.map((instructor) => instructor.id);

      if (this.allVisibleSelected) {
        this.selectedInstructorIds = this.selectedInstructorIds.filter(
          (id) => !visibleIds.includes(id)
        );
        return;
      }

      this.selectedInstructorIds = Array.from(
        new Set([...this.selectedInstructorIds, ...visibleIds])
      );
    },
    async bulkUpdateInstructorStatus(statusId) {
      if (!this.hasSelection || this.bulkProcessing) return;

      this.bulkProcessing = true;
      const selectedIds = [...this.selectedInstructorIds];
      try {
        await instructorService.bulkUpdateStatus(selectedIds, statusId);
        this.selectedInstructorIds = [];
        await Promise.all([this.loadInstructors(), this.loadCounts()]);
      } catch (error) {
        this.showError(
          this.getErrorMessage(
            error,
            this.$t("general.instructor_status_update_failed")
          )
        );
      } finally {
        this.bulkProcessing = false;
      }
    },
    async bulkDeleteInstructors() {
      if (!this.hasSelection || this.bulkProcessing) return;

      const isConfirmed = await confirmDialogDelete({ context: this });
      if (!isConfirmed) return;

      this.bulkProcessing = true;
      const selectedIds = [...this.selectedInstructorIds];
      try {
        await instructorService.bulkRemove(selectedIds);
        this.selectedInstructorIds = [];
        await Promise.all([this.loadInstructors(), this.loadCounts()]);
        if (!this.instructors.length && this.page > 1) {
          this.page -= 1;
          await this.loadInstructors();
        }
      } catch (error) {
        this.showError(
          this.getErrorMessage(
            error,
            this.$t("general.instructor_delete_failed")
          )
        );
      } finally {
        this.bulkProcessing = false;
      }
    },
    async loadInstructors() {
      this.loading = true;
      this.loadError = "";

      try {
        const result = await instructorService.list({
          ...this.buildFilterParams(undefined),
          page: this.page,
          per_page: this.perPage,
          sort: this.sortField,
          order: this.sortDirection,
        });
        this.instructors = result.items;
        this.pagination = result.pagination;
      } catch (error) {
        this.loadError = this.getErrorMessage(
          error,
          this.$t("general.instructor_list_load_failed")
        );
      } finally {
        this.loading = false;
      }
    },
    showRouteSuccess() {
      if (this.$route.query.action === "created") {
        this.showSuccess(this.$t("general.instructor_created_successfully"));
      } else if (this.$route.query.action === "updated") {
        this.showSuccess(this.$t("general.instructor_updated_successfully"));
      }
    },
    async loadCounts() {
      try {
        this.counts = await instructorService.counts();
      } catch (error) {
        this.counts = { active: 0, passive: 0, total: 0 };
      }
    },
    async loadCompanyTagOptions() {
      this.companyTagOptionsLoading = true;
      try {
        const filters = this.currentCompanyId
          ? { company_id: this.currentCompanyId }
          : {};

        await Promise.all([
          this.$store.dispatch(`${POSITION_MODULE}/${GET_POSITIONS}`, {
            url: POSITION_BASE_URL,
            filters,
          }),
          this.$store.dispatch(`${TITLE_MODULE}/${GET_TITLES}`, {
            url: TITLE_BASE_URL,
            filters,
          }),
          this.$store.dispatch(`${REGION_MODULE}/${GET_REGIONS}`, {
            url: REGION_BASE_URL,
            filters,
          }),
          this.$store.dispatch(`${DEPARTMENT_MODULE}/${GET_DEPARTMENTS}`, {
            url: DEPARTMENT_BASE_URL,
            filters,
          }),
          this.$store.dispatch(`${BRANCH_MODULE}/${GET_BRANCHES}`, {
            url: BRANCH_BASE_URL,
            filters,
          }),
        ]);
      } finally {
        this.companyTagOptionsLoading = false;
      }
    },
    getSelectedIdsParam(items) {
      if (!items.length) return undefined;
      return JSON.stringify(items.map((item) => item.id));
    },
    buildFilterParams(emptyValue) {
      return {
        like: this.filters.search || emptyValue,
        type: this.filters.type || emptyValue,
        status_id:
          this.filters.status === "" ? emptyValue : this.filters.status,
        title_id:
          this.getSelectedIdsParam(this.filters.titles) || emptyValue,
        position_id:
          this.getSelectedIdsParam(this.filters.positions) || emptyValue,
        region_id:
          this.getSelectedIdsParam(this.filters.regions) || emptyValue,
        department_id:
          this.getSelectedIdsParam(this.filters.departments) || emptyValue,
        branch_id:
          this.getSelectedIdsParam(this.filters.branches) || emptyValue,
        corporate_id:
          this.getSelectedIdsParam(this.filters.corporates) || emptyValue,
      };
    },
    clearListFilters() {
      this.filterDraft = createFilterSelection();
      this.filters = {
        ...this.filters,
        ...createFilterSelection(),
      };
      this.page = 1;
      this.closeFilterDropdown();
      this.loadInstructors();
    },
    applyFilters() {
      this.filters = {
        ...this.filters,
        type: this.filterDraft.type,
        status: this.filterDraft.status,
        titles: [...this.filterDraft.titles],
        positions: [...this.filterDraft.positions],
        regions: [...this.filterDraft.regions],
        departments: [...this.filterDraft.departments],
        branches: [...this.filterDraft.branches],
        corporates: [...this.filterDraft.corporates],
      };
      this.page = 1;
      this.closeFilterDropdown();
      this.loadInstructors();
    },
    closeFilterDropdown() {
      const dropdownElement = this.$refs.instructorFilterDropdown;
      const uiKit = window.UIkit;
      if (!dropdownElement || !uiKit) return;

      uiKit.dropdown(dropdownElement).hide(0);
    },
    setPage(page) {
      this.page = page;
      this.loadInstructors();
      window.scrollTo({ top: 0, behavior: "smooth" });
    },
    sortInstructors(order, field) {
      this.sortDirection = order;
      this.sortField = field;
      this.page = 1;
      this.loadInstructors();
    },
    async exportInstructors() {
      if (this.isReportDownloading) return;

      this.isReportDownloading = true;
      try {
        const response = await instructorService.export({
          ...this.buildFilterParams(null),
          sort: this.sortField,
          order: this.sortDirection,
        });
        const fileUrl = window.URL.createObjectURL(new Blob([response.data]));
        const fileLink = document.createElement("a");
        const date = new Date();
        const timestamp = [
          date.getFullYear(),
          String(date.getMonth() + 1).padStart(2, "0"),
          String(date.getDate()).padStart(2, "0"),
          String(date.getHours()).padStart(2, "0"),
          String(date.getMinutes()).padStart(2, "0"),
          String(date.getSeconds()).padStart(2, "0"),
        ].join("");

        fileLink.href = fileUrl;
        fileLink.setAttribute("download", `instructors_list_${timestamp}.xlsx`);
        document.body.appendChild(fileLink);
        fileLink.click();
        fileLink.remove();
        window.URL.revokeObjectURL(fileUrl);
      } catch (error) {
        this.showError(
          this.getErrorMessage(
            error,
            this.$t("general.instructor_export_failed")
          )
        );
      } finally {
        this.isReportDownloading = false;
      }
    },
    getTypeLabel(type) {
      return type === INSTRUCTOR_TYPES.EXTERNAL
        ? this.$t("general.external_instructor")
        : this.$t("general.internal_instructor");
    },
    getStatusLabel(status) {
      return Number(status) === INSTRUCTOR_STATUSES.PASSIVE
        ? this.$t("general.passive")
        : this.$t("general.active");
    },
    getStatusClass(status) {
      return Number(status) === INSTRUCTOR_STATUSES.PASSIVE
        ? "passive"
        : "active";
    },
    getExpertiseTooltip(expertiseAreas) {
      return `title: ${expertiseAreas.join(", ")}; pos: top`;
    },
    formatDate(date) {
      if (!date) return "—";
      return new Intl.DateTimeFormat("tr-TR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      }).format(new Date(date));
    },
    useFallbackPhoto(event) {
      if (event.target.src !== FALLBACK_PHOTO)
        event.target.src = FALLBACK_PHOTO;
    },
    goToCreatePage() {
      this.$router.push({ name: "company.instructors.add" });
    },
    goToEditPage(instructor) {
      this.$router.push({
        name: "company.instructors.edit",
        params: { id: instructor.id },
      });
    },
    openProfileModal(instructor) {
      if (!instructor?.id) return;

      this.profileTarget = instructor;
      this.$router.push({
        name: "company.instructors.list",
        query: {
          ...this.$route.query,
          profile: instructor.id,
        },
      }).catch(() => {});
    },
    async openProfileModalFromUrl(instructorId) {
      const instructor = this.instructors.find(
        item => Number(item.id) === Number(instructorId)
      ) || { id: Number(instructorId) };
      const requestId = ++this.profileRequestId;

      this.profileTarget = instructor;
      this.profileLoading = true;
      try {
        const detail = await instructorService.get(instructor.id);
        if (
          requestId === this.profileRequestId
          && Number(this.$route.query.profile) === Number(instructorId)
        ) {
          this.profileTarget = detail;
        }
      } catch (error) {
        if (requestId === this.profileRequestId) {
          this.showError(
            this.getErrorMessage(
              error,
              this.$t("general.instructor_detail_load_failed")
            )
          );
        }
      } finally {
        if (requestId === this.profileRequestId) {
          this.profileLoading = false;
        }
      }
    },
    closeProfileModal() {
      this.profileRequestId += 1;
      this.profileTarget = null;
      this.profileLoading = false;

      if (this.$route.query.profile) {
        const query = { ...this.$route.query };
        delete query.profile;
        this.$router.replace({
          name: "company.instructors.list",
          query,
        }).catch(() => {});
      }
    },
    editProfileTarget() {
      const instructor = this.profileTarget;
      this.closeProfileModal();
      this.goToEditPage(instructor);
    },
    async toggleInstructorStatus(instructor) {
      const nextStatus =
        instructor.statusId === INSTRUCTOR_STATUSES.ACTIVE
          ? INSTRUCTOR_STATUSES.PASSIVE
          : INSTRUCTOR_STATUSES.ACTIVE;
      try {
        await instructorService.updateStatus(instructor.id, nextStatus);
        await Promise.all([this.loadInstructors(), this.loadCounts()]);
        this.showSuccess(
          nextStatus === INSTRUCTOR_STATUSES.ACTIVE
            ? this.$t("general.instructor_activated_successfully")
            : this.$t("general.instructor_deactivated_successfully")
        );
      } catch (error) {
        this.showError(
          this.getErrorMessage(
            error,
            this.$t("general.instructor_status_update_failed")
          )
        );
      }
    },
    async deleteInstructor(instructor) {
      const isConfirmed = await confirmDialogDelete({ context: this });
      if (!isConfirmed) return;

      try {
        await instructorService.remove(instructor.id);
        this.selectedInstructorIds = this.selectedInstructorIds.filter(
          (id) => id !== instructor.id
        );
        if (this.instructors.length === 1 && this.page > 1) this.page -= 1;
        await Promise.all([this.loadInstructors(), this.loadCounts()]);
      } catch (error) {
        this.showError(
          this.getErrorMessage(
            error,
            this.$t("general.instructor_delete_failed")
          )
        );
      }
    },
    showSuccess(message) {
      this.showNotification(message, "success");
    },
    showError(message) {
      this.showNotification(message, "error");
    },
    showNotification(message, type) {
      this.notificationMessage = message;
      this.notificationType = type;
      window.clearTimeout(this.notificationTimer);
      this.notificationTimer = window.setTimeout(() => {
        this.notificationMessage = "";
      }, 3000);
    },
    getErrorMessage(error, fallback) {
      const responseData = error?.response?.data;
      if (responseData?.message) return responseData.message;
      if (responseData?.error) return responseData.error;
      if (responseData?.errors) {
        return Object.values(responseData.errors).flat().join(" ");
      }
      return error?.message || fallback;
    },
  },
  async mounted() {
    await this.loadInstructors();
    this.loadCounts();
    this.loadCompanyTagOptions();
    this.showRouteSuccess();

    const instructorId = Number(this.$route.query.profile);
    if (Number.isFinite(instructorId) && instructorId > 0) {
      this.openProfileModalFromUrl(instructorId);
    }
  },
  beforeDestroy() {
    window.clearTimeout(this.notificationTimer);
    window.clearTimeout(this.filterTimer);
  },
};
</script>

<style scoped>
@import "~vue-multiselect/dist/vue-multiselect.min.css";

.instructor-toolbar {
  display: flex;
  align-items: center;
  width: 100%;
}

.instructor-toolbar-actions {
  align-items: center;
  flex-shrink: 0;
}

.instructor-toolbar-actions select {
  min-height: 34px;
}

.instructor-search {
  width: auto;
  flex: 1;
  margin-right: 12px;
}

.instructor-search i {
  top: 9px;
  left: 10px;
  z-index: 1;
}

.instructor-search input {
  padding-left: 34px;
}

.instructor-filter-dropdown.large {
  width: min(900px, calc(100vw - 30px)) !important;
  padding: 24px;
  overflow: visible !important;
}

.instructor-filter-title {
  color: #8a8d96;
  font-weight: 700;
}

.instructor-filter-title i {
  color: #73c92d;
}

.instructor-filter-container {
  display: flex;
  min-height: 185px;
  flex-direction: column;
  justify-content: space-between;
  overflow: visible !important;
}

.instructor-filter-container h5 {
  color: #8a8d96;
}

.instructor-filter-select {
  min-height: 43px;
  border-color: #dfe3e8;
  color: #667085;
}

.instructor-filter-container ::v-deep .multiselect__input,
.instructor-filter-container ::v-deep .multiselect__single {
  border: 0 !important;
  outline: 0 !important;
  background: transparent !important;
  box-shadow: none !important;
}

.instructor-filter-actions {
  padding-top: 8px;
}

.instructor-status-options {
  display: flex;
  min-height: 43px;
  align-items: center;
  gap: 14px;
}

.instructor-status-options label {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin: 0;
  color: #344054;
  font-weight: 600;
}

.instructor-filter-button--active {
  color: #2f6fe4;
  background: #edf3ff;
}

.instructor-bulk-summary {
  align-items: center;
  min-height: 34px;
  font-size: 14px;
}

.instructor-bulk-actions {
  flex-wrap: wrap;
  gap: 8px;
}

.instructor-bulk-actions .btn {
  margin-left: 0 !important;
  margin-right: 0 !important;
}

.instructor-bulk-actions .btn:first-child {
  margin-right: auto !important;
}

.instructor-checkbox {
  width: 15px;
  height: 15px;
  margin: 10px 10px 10px 0;
}

.instructor-list-state {
  min-height: 260px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 10px;
  color: #718096;
}

.instructor-list-state i {
  font-size: 30px;
}

.instructor-table th,
.instructor-table td {
  vertical-align: middle;
}

.instructor-identity {
  min-width: 205px;
}

.instructor-avatar {
  width: 48px;
  height: 48px;
  flex-shrink: 0;
  border: 1px solid #e2e8f0;
  border-radius: 50%;
  background: #eef2f6;
  object-fit: cover;
  object-position: center top;
}

.instructor-name-button {
  padding: 0;
  border: 0;
  background: transparent;
  color: #344054;
  font-size: 13px;
  font-weight: 700;
  text-align: left;
  cursor: pointer;
}

.instructor-name-button:hover {
  color: #2f6fe4;
}

.instructor-name-button:disabled {
  color: #344054;
  cursor: default;
}

.instructor-title {
  display: inline-block;
  min-width: 150px;
  color: #475467;
  font-size: 12px;
}

.instructor-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
  min-width: 170px;
}

.instructor-tag {
  display: inline-flex;
  align-items: center;
  padding: 4px 7px;
  border-radius: 6px;
  background: #f1f5f9;
  color: #526174;
  font-size: 10px;
  font-weight: 600;
}

.instructor-tag--more {
  background: #e8effd;
  color: #356ac3;
}

.instructor-type,
.instructor-status {
  display: inline-flex;
  align-items: center;
  padding: 5px 8px;
  border-radius: 999px;
  font-size: 10px;
  font-weight: 700;
  white-space: nowrap;
}

.instructor-type--internal {
  background: #edf3ff;
  color: #356ac3;
}

.instructor-type--external {
  background: #f4edff;
  color: #7a4ab5;
}

.instructor-status {
  gap: 5px;
}

.instructor-status--active {
  background: #e8f7ef;
  color: #25845a;
}

.instructor-status--passive {
  background: #f2f4f7;
  color: #667085;
}

.instructor-status__dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: currentColor;
}

.instructor-date {
  color: #667085;
  font-size: 11px;
  white-space: nowrap;
}

.instructor-actions {
  display: inline-flex;
  gap: 2px;
  min-width: 144px;
  justify-content: flex-end;
}

@media (max-width: 900px) {
  .instructor-toolbar {
    flex-wrap: wrap;
    gap: 10px;
  }

  .instructor-search {
    width: 100%;
  }

  .instructor-toolbar-actions {
    width: 100%;
    justify-content: flex-end;
  }

}

@media (max-width: 640px) {
  .instructor-toolbar-actions {
    flex-wrap: wrap;
  }

  .instructor-bulk-summary {
    align-items: flex-start;
    flex-direction: column;
    gap: 6px;
  }

  .instructor-bulk-actions .btn {
    width: 100%;
  }

  .instructor-bulk-actions .btn:first-child {
    margin-right: 0 !important;
  }

}
</style>
